export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://scheduleapp-backend.bluemeadow-93601b94.westeurope.azurecontainerapps.io";

const TOKEN_KEY = "access_token";
const USER_KEY = "auth_user";

function getAccessToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function buildHeaders(extraHeaders = {}, isJson = true) {
  const token = getAccessToken();

  return {
    ...(isJson ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
}

async function request(path, options = {}) {
  const url = `${API_BASE_URL}${path}`;

  const isFormData = options.body instanceof FormData;

  const res = await fetch(url, {
    ...options,
    headers: buildHeaders(options.headers || {}, !isFormData),
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const body = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    const err = new Error(
      (body && body.error) ||
      (typeof body === "string" && body) ||
      `Request failed (${res.status})`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

// ---------- Files ----------
export async function getFiles() {
  return request("/api/files", { method: "GET", headers: buildHeaders() });
}

export async function getActiveSchedule() {
  return request("/api/files/active", { method: "GET", headers: buildHeaders() });
}

export async function setActiveSchedule(filename) {
  return request("/api/files/active", {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify({ filename }),
  });
}

// ---------- Import ----------
export async function importFileIntoDb(filename) {
  // POST /api/session-data/import { filename }
  return request("/api/session-data/import", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ filename }),
  });
}

// ---------- Calendar / session_data ----------
export async function getSessionData(filename) {
  // GET /api/session-data/:file -> { sessions: [...] }
  return request(`/api/session-data/${encodeURIComponent(filename)}`, { method: "GET", headers: buildHeaders() });
}

export async function addSessionData(filename, payload) {
  // POST /api/session-data/:file/add
  return request(`/api/session-data/${encodeURIComponent(filename)}/add`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function updateSessionData(filename, payload) {
  // PUT /api/session-data/:file
  return request(`/api/session-data/${encodeURIComponent(filename)}`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function deleteSessionData(filename, payload) {
  // DELETE /api/session-data/:file
  return request(`/api/session-data/${encodeURIComponent(filename)}`, {
    method: "DELETE",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

// ---------- Stats ----------
function buildStatsQuery(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    const v = Array.isArray(value) ? value.join(",") : value;
    if (v !== undefined && v !== null && v !== "") qs.set(key, String(v));
  });
  return qs.toString();
}

export async function getStatsHours(params) {
  // GET /api/stats/hours?filename=&group=&granularity=&from=&to=&ids=
  return request(`/api/stats/hours?${buildStatsQuery(params)}`, { method: "GET", headers: buildHeaders() });
}

export async function getStatsDistance(params) {
  // GET /api/stats/distance?filename=&granularity=&from=&to=&employees=
  return request(`/api/stats/distance?${buildStatsQuery(params)}`, { method: "GET", headers: buildHeaders() });
}

export async function warmTravelData(filename) {
  // POST /api/geo/warm { filename } -> { status: "started" | "queued" }
  return request("/api/geo/warm", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ filename }),
  });
}

// ---------- Lookups ----------
export async function getUniqueNames() {
  // GET /api/unique-names -> { employees: [...], clients: [...] }
  return request("/api/unique-names", { method: "GET", headers: buildHeaders() });
}

export async function getEmployees() {
  // GET /api/employees -> [...]
  return request("/api/employees", { method: "GET", headers: buildHeaders() });
}

export async function getClients() {
  // GET /api/clients -> [...]
  return request("/api/clients", { method: "GET", headers: buildHeaders() });
}

// ---------- DataPage: Clients ----------
export async function listClients() {
  return request("/api/clients", { method: "GET", headers: buildHeaders() });
}

export async function createClient(payload) {
  return request("/api/clients", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function updateClient(id, payload) {
  return request(`/api/clients/${id}`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function deleteClientById(id) {
  return request(`/api/clients/${id}`, { method: "DELETE", headers: buildHeaders() });
}

export async function bulkUpdateClientsDateRange(start_datetime, end_datetime) {
  return request("/api/clients/bulk-date-range", {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify({ start_datetime, end_datetime }),
  });
}

// ---------- DataPage: Employees ----------
export async function listEmployees() {
  return request("/api/employees", { method: "GET", headers: buildHeaders() });
}

export async function createEmployee(payload) {
  return request("/api/employees", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function updateEmployee(id, payload) {
  return request(`/api/employees/${id}`, {
    method: "PUT",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function deleteEmployeeById(id) {
  return request(`/api/employees/${id}`, { method: "DELETE", headers: buildHeaders() });
}

export async function publishSiosCsv(file) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${API_BASE_URL}/api/sios/publish`, {
    method: "POST",
    headers: buildHeaders({}, false),
    body: formData,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const err = new Error(
      (body && body.error) ||
      (typeof body === "string" && body) ||
      `Request failed (${res.status})`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

export async function publishSiosFilename(filename) {
  return request("/api/sios/publish/from-file", {
    method: "POST",
    body: JSON.stringify({ filename }),
  });
}

export async function deleteSiosServicesByDateRange(start_date, end_date) {
  return request("/api/sios/publish/delete-range", {
    method: "POST",
    body: JSON.stringify({ start_date, end_date }),
  });
}

export async function dispatchWhatsappByDateRange(startDate, endDate) {
  const res = await fetch(`${API_BASE_URL}/api/whatsapp-dispatch`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
    }),
  });

  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    const err = new Error(
      (body && body.error) ||
      (typeof body === "string" && body) ||
      `Request failed (${res.status})`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

export async function dismissWhatsappNotification(notificationId) {
  return request(`/api/whatsapp-dispatch/notifications/${notificationId}/dismiss`, {
    method: "POST",
    headers: buildHeaders(),
  });
}

export async function getWhatsappNotifications(filters = {}) {
  const params = new URLSearchParams();

  if (filters.file_id) params.append("file_id", filters.file_id);
  if (filters.status) params.append("status", filters.status);
  if (filters.limit) params.append("limit", String(filters.limit));

  const qs = params.toString();
  const path = qs
    ? `/api/whatsapp/notifications?${qs}`
    : "/api/whatsapp/notifications";

  return request(path, { method: "GET" });
}

export async function getEmployeeWhatsappNotifications(filters = {}) {
  const params = new URLSearchParams();

  if (filters.status) params.append("status", filters.status);
  if (filters.limit) params.append("limit", String(filters.limit));

  const qs = params.toString();
  const path = qs
    ? `/api/whatsapp/employee-notifications?${qs}`
    : "/api/whatsapp/employee-notifications";

  return request(path, { method: "GET" });
}

export async function dismissEmployeeWhatsappNotification(notificationId) {
  return request(`/api/whatsapp-dispatch/employee-notifications/${notificationId}/dismiss`, {
    method: "POST",
    headers: buildHeaders(),
  });
}

export async function loginUser(payload) {
  return request("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function registerUser(payload) {
  return request("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getCurrentUser() {
  return request("/api/auth/me", {
    method: "GET",
  });
}

export function logoutUser() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function saveUser(user) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function loadStoredUser() {
  return getStoredUser();
}

export async function getAdminUsers() {
  return request("/api/auth/users", {
    method: "GET",
  });
}

export async function setAdminUserActive(userId, isActive) {
  return request(`/api/auth/users/${userId}/active`, {
    method: "PATCH",
    body: JSON.stringify({ is_active: isActive }),
  });
}

// ---------- API keys (Admin only, JWT only) ----------
// These endpoints are declared allow_api_key=False on the backend, so they are
// reachable with a signed-in Admin's token and never with an API key. That is
// what stops a leaked key from minting more keys.

export async function getApiKeys() {
  return request("/api/api-keys", { method: "GET", headers: buildHeaders() });
}

export async function createApiKey(payload) {
  // payload: { label, role, employee_number?, expires_in_days? }
  // The response carries `api_key` — the only time the secret exists.
  return request("/api/api-keys", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}

export async function revokeApiKey(keyId) {
  return request(`/api/api-keys/${keyId}/revoke`, {
    method: "POST",
    headers: buildHeaders(),
  });
}

export async function startModelRun(result_filename) {
  return request("/api/model", {
    method: "POST",
    body: JSON.stringify({ result_filename }),
  });
}

export async function startModelRerun(input_filename, cutoff_date, result_filename) {
  return request("/api/model/from_file", {
    method: "POST",
    body: JSON.stringify({
      input_filename,
      cutoff_date,
      result_filename,
    }),
  });
}

export async function getJobStatus(jobId) {
  return request(`/api/job/${jobId}`, {
    method: "GET",
    headers: buildHeaders(),
  });
}

export async function deleteGeneratedFile(filename) {
  return request(`/api/files/${encodeURIComponent(filename)}`, {
    method: "DELETE",
    headers: buildHeaders(),
  });
}

export async function downloadGeneratedFile(filename) {
  // The endpoint requires a token, and a browser navigation (<a href>) cannot
  // carry an Authorization header — so fetch the file and hand back a Blob for
  // the caller to turn into an object URL.
  const url = `${API_BASE_URL}/api/files/download/${encodeURIComponent(filename)}`;
  const res = await fetch(url, { headers: buildHeaders({}, false) });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && body.error) message = body.error;
    } catch {
      // error body was not JSON; keep the status-based message
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  return res.blob();
}

export async function dispatchEmployeeScheduleImages(payload) {
  // payload: { week_label, week_start_date, week_end_date, employee_schedules: [{ employee_name, employee_number, image_base64 }] }
  return request("/api/whatsapp-dispatch/employee-schedules", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getEmployeeNotificationCoverage(employeeNumber, date) {
  const params = new URLSearchParams({ employee_number: employeeNumber, date });
  return request(`/api/whatsapp-dispatch/employee-notification-coverage?${params.toString()}`, {
    method: "GET",
    headers: buildHeaders(),
  });
}

export async function dispatchEmployeeScheduleChange(payload) {
  // payload: { employee_name, employee_number, week_label, week_start_date, week_end_date,
  //            change_type, change_description, reason, image_base64 }
  return request("/api/whatsapp-dispatch/employee-schedule-change", {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
}